import { useFocusEffect } from "@react-navigation/native";
import { Button, Page } from "@towersystems/roam-common-native";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useRemove2faStrategy } from "../../../../utilities/2fa/hooks";
import { useTranslation } from "../../../../utilities/i18n";
import { userIsAdmin } from "../../../../utilities/user-is-admin";
import { EnterMfaCodeFieldset } from "../../components/Remove2faForm/components/EnterMfaCodeFieldset";
import { Remove2faFormValues } from "../../components/Remove2faForm/types";

export const RemoveNative2faVerifyCodeScreen = ({ navigation }: any) => {
  const { t } = useTranslation("settings");
  const { user } = useAuthenticationProvider();
  const { handleOnSubmit } = useRemove2faStrategy();

  const { handleSubmit, formState, getValues, reset } = useFormContext<any>();

  const { isLoading, isSubmitting, isValid, isSubmitSuccessful } = formState;

  const disabled = isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  function handleComplete() {
    if (!user) return;
    const isOwner = userIsAdmin(user);

    // owner has access to general settings screen
    // staff do not

    const dynamicRoutes = [
      { name: "SettingsLandingScreen" },
      isOwner && {
        name: "GeneralSettingsScreen",
      },
      {
        name: "SetupNative2faRootScreen",
        state: {
          routes: [{ name: "ListNative2faMethodsScreen" }],
        },
      },
    ];

    navigation.reset({
      index: 0,
      routes: [
        {
          name: "SettingsRootScreen",
          state: {
            routes: dynamicRoutes,
          },
        },
      ],
    });
  }

  async function handleFinalSubmit(values: Remove2faFormValues) {
    return handleOnSubmit(values)?.then((success) => {
      if (success) {
        handleComplete();
      }
    });
  }

  // undo any form state from future steps if we come back to this screen
  // it also resets the isSubmitSuccessful flag on the form ready
  // for a new submission
  useFocusEffect(
    useCallback(() => {
      const values = getValues();
      reset({
        ui: {
          ...values.ui,
        },
        data: {
          ...values.data,
        },
      });
    }, [reset])
  );

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.remove_2fa_verify_code")}
      loading={isSubmitting}
      isDestructive={true}
      onPress={handleSubmit(handleFinalSubmit)}
    ></Button>
  );

  return (
    <Page
      title={t("title.remove_2fa_verify_code")}
      secondaryTitle={t("title.remove_2fa_verify_code_secondary")}
      footer={footerMarkup}
    >
      <EnterMfaCodeFieldset />
    </Page>
  );
};
